"use client";

import { FC, ReactNode, useRef, useState } from "react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { CalendarEvent, downloadEventIcs, getGoogleCalendarUrl, getOutlookCalendarUrl, getOutlookWorkCalendarUrl } from "@/utils/calendarUtils";

type CalendarProvider = "google" | "outlook" | "outlookWork" | "apple";

interface AddToCalendarButtonProps {
  label: string;
  event: CalendarEvent;
  buttonClassName?: string;
  onSelect?: (provider: CalendarProvider) => void;
  size?: ButtonSize;
  variant?: ButtonVariant;
  theme?: ButtonTheme;
  icon?: ReactNode;
  /** Which side of the trigger the provider panel opens on. Defaults to "bottom" (existing behavior). */
  panelPosition?: "top" | "bottom";
}

const providers: { id: CalendarProvider; label: string }[] = [
  { id: "google", label: "Google Calendar" },
  { id: "outlook", label: "Outlook.com" },
  { id: "outlookWork", label: "Outlook (Work/School)" },
  { id: "apple", label: "Apple Calendar" },
];

const AddToCalendarButton: FC<AddToCalendarButtonProps> = ({
  label,
  event,
  buttonClassName,
  onSelect,
  size = ButtonSize.SMALL,
  variant = ButtonVariant.TERTIARY,
  theme = ButtonTheme.WHITE,
  icon,
  panelPosition = "bottom",
}) => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useOutsideClick(panelRef, () => setOpen(false));

  const handleSelect = (provider: CalendarProvider) => {
    onSelect?.(provider);
    if (provider === "google") {
      const url = getGoogleCalendarUrl(event);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } else if (provider === "outlook") {
      const url = getOutlookCalendarUrl(event);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } else if (provider === "outlookWork") {
      const url = getOutlookWorkCalendarUrl(event);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } else {
      downloadEventIcs(event);
    }
    setOpen(false);
  };

  return (
    <div className="relative inline-block" ref={panelRef}>
      <Button
        className={buttonClassName || "text-center"}
        type="button"
        size={size}
        variant={variant}
        theme={theme}
        onClick={() => setOpen((prev) => !prev)}
      >
        {icon ? (
          <span className="flex items-center justify-center gap-xs">
            {icon}
            {label}
          </span>
        ) : (
          label
        )}
      </Button>
      {open ? (
        <div className={`absolute z-40 ${panelPosition === "top" ? "bottom-full mb-xs" : "top-full mt-xs"} left-1/2 -translate-x-1/2 w-max`}>
          <div className="bg-white shadow-strong text-black rounded-lg py-xs min-w-[180px]">
            {providers.map((provider) => (
              <button
                key={provider.id}
                type="button"
                className="block w-full text-left px-m py-xs hover:bg-gold hover:text-white text-s"
                onClick={() => handleSelect(provider.id)}
              >
                {provider.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AddToCalendarButton;
